/**
 * @fileoverview Text width measurement for the PDF field editor's sample
 * preview, so it can mirror the fill action's shrink-to-fit layout
 * (`layoutTextInBox` in convex/jobs/precedents/fillPdfCore).
 *
 * The fill action measures with pdf-lib's embedded Helvetica; the browser
 * preview measures the same text with a canvas 2D context in Helvetica /
 * Arial. Where no canvas is available (tests, unusual environments) glyphs
 * are approximated at half the point size each — the same rule the core's
 * unit tests use.
 */

import type { TextMetrics } from "~/convex/jobs/precedents/fillPdfCore";

/**
 * The preview draws and measures in the same face the fill embeds
 * (pdf-lib's standard Helvetica), so a value that fits on screen fits in the
 * PDF. Arial is the metric-compatible fallback where Helvetica is absent.
 */
export const PREVIEW_FONT_FAMILY = "Helvetica, Arial, sans-serif";

/** Width at `size` from a canvas measurement taken at a reference size. */
export function scaledWidth(
  referenceWidth: number,
  referenceSize: number,
  size: number,
): number {
  return referenceSize > 0 ? (referenceWidth * size) / referenceSize : 0;
}

/**
 * Metrics backed by a canvas 2D context. Returns an approximation-only
 * implementation when the canvas API is unavailable.
 */
export function createPreviewTextMetrics(): Pick<
  TextMetrics,
  "widthOfTextAtSize"
> {
  const context = (() => {
    try {
      return typeof document === "undefined"
        ? null
        : document.createElement("canvas").getContext("2d");
    } catch {
      return null;
    }
  })();
  if (!context) {
    return { widthOfTextAtSize: (text, size) => text.length * size * 0.5 };
  }
  const referenceSize = 100;
  return {
    widthOfTextAtSize: (text, size) => {
      context.font = `${referenceSize}px ${PREVIEW_FONT_FAMILY}`;
      return scaledWidth(context.measureText(text).width, referenceSize, size);
    },
  };
}
